import { Box, Flex } from "@chakra-ui/react";
import { useRef, useState, useCallback } from "react";
import useFileManager from "./useFileManager";
import useFolderFilesManager from "./useFileManager/Files";
import { useLang } from "../../Hooks";
import DebugErrorBoundary from "./DebugErrorBoundary"; // [DEBUG]
// [FIX] كل lazy() + Suspense داخل FileManager بتسبب React #321 مع Chakra v2 + React 19
// السبب: useColorMode من Chakra بيتكسر أثناء Suspense replay في React 19
import FoldersManager from "./Folders";
import ViewManager from "./View";
import CreateFolder from "./Modals/Create";
import EditFolderModal from "./Modals/Edit";
import DeleteModal from "./Modals/Delete";
import EditFileModal from "./Modals/EditFile";
import DeleteFileModal from "./Modals/DeleteFile";
import UploadFiles from "./Modals/Upload";
import ViewFileModal from "./Modals/ViewFile";

const FileManagerContent = ({ ContractData, Tab }) => {
  const { texts } = useLang();
  const {
    Folders,
    FolderData,
    OnRefetchFolders,
    OnRefetchFolder,
    IsFoldersLoading,
    IsFolderContentsLoading,
    IsAddFolderLoading,
    IsUpdateFolderLoading,
    IsDeleteFolderLoading,
    State,
    SelectedFolder,
    OnSubmit,
    OnChange,
    OnSelectFolder,
    OpenFolders,
    OnToggleFolder,
  } = useFileManager({ ContractData, Tab });

  const {
    OnSubmit: OnSubmitFile,
    IsUpdateFolderFilesLoading,
    IsDeleteFolderFilesLoading,
  } = useFolderFilesManager();

  const handleFileSubmit = async (form) => {
    const success = await OnSubmitFile(form);
    if (success) {
      OnRefetchFolder();
    }
    return success;
  };

  const [sidebarWidth, setSidebarWidth] = useState(280);
  const isResizing = useRef(false);

  const handleMouseMove = useCallback((e) => {
    if (isResizing.current) {
      const newWidth = e.clientX;
      if (newWidth > 240 && newWidth < window.innerWidth / 2) {
        setSidebarWidth(newWidth);
      }
    }
  }, []);

  const handleMouseUp = useCallback(() => {
    isResizing.current = false;
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    isResizing.current = true;
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  const pageHeight = { base: "auto", md: "calc(100dvh - 7rem)" };

  return (
    <Box>
      <Flex
        gap={1.5}
        h={pageHeight}
        flexDirection={{ base: "column", md: "row" }}
      >
        <Box w={`${sidebarWidth}px`}>
          {/* [DEBUG] FoldersManager — direct import (not lazy) to avoid Chakra+React19 Suspense bug */}
          <DebugErrorBoundary label="FoldersManager">
            <FoldersManager
              OnRefetch={OnRefetchFolders}
              OnSelect={OnSelectFolder}
              OnChange={OnChange}
              OnToggleFolder={OnToggleFolder}
              OpenFolders={OpenFolders}
              Selected={SelectedFolder}
              Props={{
                Folders,
                IsFoldersLoading: IsFoldersLoading,
              }}
              texts={texts}
            />
          </DebugErrorBoundary>
        </Box>
        <Box
          w="5px"
          cursor="col-resize"
          onMouseDown={handleMouseDown}
          borderRadius="full"
          display={{
            base: "none",
            md: "block",
          }}
          sx={{
            bg: "var(--resizer-bg)",
            _hover: {
              bg: "var(--resizer-hover-bg)",
            },
          }}
        />
        <Box flex="1">
          {/* [DEBUG] ViewManager — direct import (not lazy) to avoid Chakra+React19 Suspense bug */}
          <DebugErrorBoundary label="ViewManager">
            <ViewManager
              SelectedFolder={SelectedFolder}
              OnChange={OnChange}
              OnSelect={OnSelectFolder}
              OnRefetch={OnRefetchFolder}
              OnSubmitFolder={OnSubmit}
              OnSubmitFile={handleFileSubmit}
              Height={pageHeight}
              Props={{
                FolderData,
                AllFolders: Folders,
                IsFoldersLoading: IsFolderContentsLoading,
              }}
              texts={texts}
            />
          </DebugErrorBoundary>
        </Box>
      </Flex>
      {/* [DEBUG] Modals — direct imports (not lazy) */}
      <DebugErrorBoundary label="Modals">
        <CreateFolder
          IsLoading={IsAddFolderLoading}
          OnSubmit={OnSubmit}
          ParentId={State?.ParentId}
          OnClose={() => OnChange("Reset")}
          IsOpen={State.IsOpen && State.Tab === "Create"}
          texts={texts}
        />
        <EditFolderModal
          IsLoading={IsUpdateFolderLoading}
          OnSubmit={OnSubmit}
          FolderData={{
            FolderId: State.FolderId,
            FolderName: State.FolderName,
          }}
          OnClose={() => OnChange("Reset")}
          IsOpen={State.IsOpen && State.Tab === "Edit"}
          texts={texts}
        />
        <DeleteModal
          IsLoading={IsDeleteFolderLoading}
          OnSubmit={OnSubmit}
          FolderId={State.FolderId}
          OnClose={() => OnChange("Reset")}
          IsOpen={State.IsOpen && State.Tab === "Delete"}
          texts={texts}
        />
        <EditFileModal
          IsLoading={IsUpdateFolderFilesLoading}
          OnSubmit={handleFileSubmit}
          FileData={{ FileId: State.FileId, FileName: State.FileName }}
          OnClose={() => OnChange("Reset")}
          IsOpen={State.IsOpen && State.Tab === "EditFile"}
          texts={texts}
        />
        <DeleteFileModal
          IsLoading={IsDeleteFolderFilesLoading}
          OnSubmit={handleFileSubmit}
          FileId={State.FileId}
          OnClose={() => OnChange("Reset")}
          IsOpen={State.IsOpen && State.Tab === "DeleteFile"}
          texts={texts}
        />
        <UploadFiles
          OnClose={() => OnChange("Reset")}
          IsOpen={State.IsOpen && State.Tab === "UploadFile"}
          OnRefetch={OnRefetchFolder}
          ParentId={SelectedFolder}
          ContractId={ContractData?.ContractId}
          texts={texts}
        />
        <ViewFileModal
          OnClose={() => OnChange("Reset")}
          IsOpen={State.IsOpen && State.Tab === "ViewFile"}
          Selected={State.File}
          texts={texts}
        />
      </DebugErrorBoundary>
    </Box>
  );
};

export default FileManagerContent;
